import { changeAppLanguage, readStoredLocale, resolveLocale, type SupportedLocale } from "@patient-watch/i18n";
import { AppLocaleProvider, useAppLocale, useLocale } from "@patient-watch/i18n/webapp/copy/LocaleContext";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/utils/auth";
import { useUserLocaleQuery } from "./api/selectUserLocale";
import { useSyncPreferredLocaleIfUnset } from "./useSyncPreferredLocaleIfUnset";

export { useAppLocale, useLocale };

export function LocaleProvider({ children }: { children: ReactNode }) {
    const auth = useAuth();
    const { data: userPreferredLocale } = useUserLocaleQuery(auth.id);
    const [programmeLocale, setProgrammeLocale] = useState<string | null | undefined>(null);
    const [guestLocaleOverride, setGuestLocaleOverride] = useState<SupportedLocale | null>(null);

    const resolvedLocale = useMemo(
        () =>
            resolveLocale({
                programmeLocale,
                userPreferredLocale,
                storedLocale: readStoredLocale(),
                navigatorLanguage: typeof navigator !== "undefined" ? navigator.language : null,
            }),
        [programmeLocale, userPreferredLocale]
    );

    useSyncPreferredLocaleIfUnset(programmeLocale, resolvedLocale);

    const locale = guestLocaleOverride ?? resolvedLocale;

    const setLocale = useCallback((next: SupportedLocale) => {
        setGuestLocaleOverride(next);
    }, []);

    useEffect(() => {
        // Keep the in-session override until the persisted preference catches up.
        // Clearing on any preferred_locale change lets a slow sync(en-GB) wipe a
        // newer LanguageSwitcher choice before reload.
        setGuestLocaleOverride((current) => {
            if (!current) return null;
            if (userPreferredLocale === current) return null;
            return current;
        });
    }, [userPreferredLocale, programmeLocale]);

    useEffect(() => {
        void changeAppLanguage(locale);
    }, [locale]);

    const value = useMemo(
        () => ({
            locale,
            programmeLocale,
            setProgrammeLocale,
            setLocale,
        }),
        [locale, programmeLocale, setLocale]
    );

    return <AppLocaleProvider value={value}>{children}</AppLocaleProvider>;
}
